import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, Formula, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.inventario-teorico-real-no-cuadra.${field}`

const SOURCES: { href?: string; labelKey: string; fallback: string }[] = [
    {
        labelKey: 'sources.1',
        fallback:
            'Rango de desviación normal del 3 % al 5 % sobre el coste de materia prima: criterio de Tipi a partir de los locales con los que trabaja.',
    },
    {
        labelKey: 'sources.2',
        fallback:
            'Umbrales de desviación medidos en puntos sobre ventas: guías publicadas por meez y Supy, fabricantes de software de gestión de cocina.',
    },
    {
        labelKey: 'sources.3',
        fallback: 'Caso del pulpo, fórmula de desviación y paso a euros: elaboración propia de Tipi.',
    },
]

export function InventarioTeoricoRealNoCuadraArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'no-se-tira', label: t(k('toc.1'), '1. «Aquí no se tira nada»') },
        { id: 'diferencia-normal', label: t(k('toc.2'), '2. ¿Cuánta diferencia es normal?') },
        { id: 'kilos-euros', label: t(k('toc.3'), '3. Compara en kilos antes que en euros') },
        { id: 'repartido-concentrado', label: t(k('toc.4'), '4. Descuadre repartido o concentrado') },
        { id: 'por-que-no-cuadra', label: t(k('toc.5'), '5. ¿Por qué no me cuadra?') },
        { id: 'a-favor', label: t(k('toc.6'), '6. ¿Y si me sale a favor?') },
        { id: 'pulpo', label: t(k('toc.7'), '7. El pulpo que nadie tiraba') },
        { id: 'limites', label: t(k('toc.8'), '8. Lo que esta rutina no te dice') },
    ]

    const pulpoRows = [
        { label: t(k('table.pulpo.1'), 'Raciones vendidas'), value: t(k('table.pulpo.1c'), '220') },
        {
            label: t(k('table.pulpo.2'), 'Pulpo por ración según la ficha'),
            value: t(k('table.pulpo.2c'), '150 g'),
        },
        {
            label: t(k('table.pulpo.3'), 'Pulpo por ración pesado en el pase'),
            value: t(k('table.pulpo.3c'), '185 g'),
        },
        {
            label: t(k('table.pulpo.4'), 'Lo que debería haberse gastado'),
            value: t(k('table.pulpo.4c'), '33 kg'),
        },
        {
            label: t(k('table.pulpo.5'), 'Lo que se gastó de verdad'),
            value: t(k('table.pulpo.5c'), '40,7 kg'),
        },
        {
            label: t(k('table.pulpo.6'), 'Desviación'),
            value: t(k('table.pulpo.6c'), '23 %'),
            strong: true,
        },
        {
            label: t(k('table.pulpo.7'), 'Diferencia en euros'),
            value: t(k('table.pulpo.7c'), '215,60 € al mes'),
            strong: true,
        },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'El inventario teórico y el real nunca cuadran al céntimo. Una desviación de entre el 3 % y el 5 % sobre lo que deberías haber gastado es normal; por encima, hay algo que buscar. Compara primero en kilos y después en euros: si solo descuadra en euros, el problema está en la oficina, no en la cocina. Si descuadra en kilos, mira en este orden: oficina, recepción, cocina y sala. Y la causa que no hemos dejado de encontrar en ningún local no es el robo ni la merma: son escandallos con cantidades puestas a ojo.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Inventario de cámara frigorífica de un restaurante con lista de productos y báscula',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGap2xl, 'text-article-muted-soft')}>
                                {t(k('published'), 'Publicado el 23 de octubre de 2026.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='no-se-tira' className={articleUi.h2}>
                        {t(k('h2.noSeTira'), '«Aquí no se tira nada»')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.noSeTira.1'),
                                'Has sacado el teórico, has contado la cámara y falta pulpo. Bastante pulpo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.noSeTira.2'), 'Se lo dices al jefe de cocina y te lo jura: aquí no se tira nada.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.noSeTira.3'), 'Probablemente tiene razón.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.noSeTira.4Strong'),
                                    'La mayoría de los descuadres no están en la cámara: están en la ficha, en el albarán o en el TPV.',
                                )}
                            </strong>{' '}
                            {t(
                                k('p.noSeTira.4After'),
                                'Si empiezas buscando en la cocina, empiezas por el sitio más caro de revisar.',
                            )}
                        </p>
                    </div>

                    <h2 id='diferencia-normal' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.diferenciaNormal'), '¿Cuánta diferencia entre teórico y real es normal?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.diferencia.1'),
                                'El inventario teórico es lo que debería quedar en tu almacén según lo que compraste, lo que vendiste y los gramos que marca cada escandallo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.diferencia.2Before'), 'La desviación se calcula ingrediente a ingrediente: ')}
                            <strong>
                                {t(
                                    k('p.diferencia.2Strong'),
                                    'lo que gastaste de verdad, menos lo que deberías haber gastado, dividido entre lo que deberías haber gastado.',
                                )}
                            </strong>
                        </p>
                        <Formula>
                            {t(k('formula.desviacion'), 'Desviación = (gasto real − gasto teórico) ÷ gasto teórico')}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.diferencia.3Strong'),
                                    'Entre un 3 % y un 5 % del coste de materia prima es lo normal en un restaurante que escandalla bien.',
                                )}
                            </strong>{' '}
                            <em>
                                {t(
                                    k('p.diferencia.3Em'),
                                    'Es el rango que vemos en los locales con los que trabajamos, no una cifra publicada por nadie.',
                                )}
                            </em>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.diferencia.4'), 'Por encima del 5 %, o por debajo de cero, toca buscar.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.diferencia.5Before'), 'Antes de buscar, la pega: ')}
                            <strong>
                                {t(k('p.diferencia.5Strong'), 'el teórico vale lo que valen tus escandallos.')}
                            </strong>{' '}
                            {t(
                                k('p.diferencia.5After'),
                                'Si la ficha se hizo a ojo, el descuadre es de la ficha y en la cámara no vas a encontrar nada.',
                            )}
                        </p>
                    </div>

                    <h2 id='kilos-euros' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.kilosEuros'), 'Paso 1. Compara en kilos antes que en euros')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.kilos.1'), 'Pon cada ingrediente en dos columnas: unidades y euros.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.kilos.2Strong'),
                                    'Si cuadra en kilos y no en euros, no falta género: falta actualizar precios.',
                                )}
                            </strong>{' '}
                            {t(
                                k('p.kilos.2After'),
                                'El aceite subió en septiembre y la ficha sigue con el precio de marzo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.kilos.3'),
                                'La trampa es arreglarlo solo en la ficha. Si el coste subió, tu margen ya bajó, y eso se corrige en la carta.',
                            )}
                        </p>
                    </div>

                    <h2 id='repartido-concentrado' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.repartido'), 'Paso 2. Mira si el descuadre está repartido o concentrado')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.repartido.1'), 'Ordena tus 20 ingredientes de más a menos desviación.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(k('p.repartido.2Strong'), 'Si todos se desvían parecido, la causa es de sistema:')}
                            </strong>{' '}
                            {t(
                                k('p.repartido.2After'),
                                'albaranes sin meter, un conteo mal hecho o ventas mal registradas. Nadie se lleva un poco de cada cosa.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.repartido.3Strong'),
                                    'Si uno o dos se disparan y el resto está en rango, la causa es física',
                                )}
                            </strong>{' '}
                            {t(k('p.repartido.3After'), 'y tiene dirección: ese producto y los platos que lo gastan.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.repartido.4'),
                                'La trampa es mirar solo el total. Un 4 % global puede esconder un ingrediente al 20 %.',
                            )}
                        </p>
                    </div>

                    <h2 id='por-que-no-cuadra' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.porQue'), '¿Por qué no me cuadra el inventario del restaurante?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.porQue.1'),
                                'Si descuadra en kilos, el género se ha ido por uno de cuatro sitios. Míralos de lo más barato de comprobar a lo más incómodo.',
                            )}
                        </p>
                    </div>

                    <h3 id='oficina' className={cn(articleUi.h3Block, 'scroll-mt-[90px]')}>
                        {t(k('h3.oficina'), 'Oficina: se arregla sin levantarte de la silla')}
                    </h3>
                    <div className={articleUi.prose}>
                        <ul className={articleUi.dashList}>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.oficina.1Strong'), 'Escandallos a ojo.')}</strong>{' '}
                                {t(
                                    k('list.oficina.1After'),
                                    '«Un puñado», «un chorro», «una ración normal». Si la cantidad no se pesó, el teórico es una suposición.',
                                )}
                            </li>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.oficina.2Strong'), 'Albaranes y abonos sin meter.')}</strong>{' '}
                                {t(
                                    k('list.oficina.2After'),
                                    'Un pedido sin albarán hace que gastes menos de lo que debías. Una devolución sin abono, que gastes más.',
                                )}
                            </li>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.oficina.3Strong'), 'El TPV.')}</strong>{' '}
                                {t(
                                    k('list.oficina.3After'),
                                    'El plato marcado con el botón de «varios» se cobra, pero no descuenta ingredientes. Para el teórico, nunca salió.',
                                )}
                            </li>
                        </ul>
                    </div>

                    <h3 id='recepcion' className={cn(articleUi.h3Block, 'scroll-mt-[90px]')}>
                        {t(k('h3.recepcion'), 'Recepción: lo que pagas no es lo que entra')}
                    </h3>
                    <div className={articleUi.prose}>
                        <ul className={articleUi.dashList}>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.recepcion.1Strong'), 'La caja que no pesa lo que pone.')}</strong>{' '}
                                {t(
                                    k('list.recepcion.1After'),
                                    'Si nadie pesa al recibir, firmas el peso del proveedor.',
                                )}
                            </li>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.recepcion.2Strong'), 'El hielo del congelado.')}</strong>{' '}
                                {t(
                                    k('list.recepcion.2After'),
                                    'El glaseado del pescado se paga a precio de pescado y en la cámara se convierte en agua.',
                                )}
                            </li>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.recepcion.3Strong'), 'El producto sustituido.')}</strong>{' '}
                                {t(
                                    k('list.recepcion.3After'),
                                    'Otro calibre u otra marca, mismo precio en el albarán.',
                                )}
                            </li>
                        </ul>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.recepcion.trampa'),
                                'La trampa: dar por bueno el peso del albarán porque el proveedor es de confianza.',
                            )}
                        </p>
                    </div>

                    <h3 id='cocina' className={cn(articleUi.h3Block, 'scroll-mt-[90px]')}>
                        {t(k('h3.cocina'), 'Cocina: porciones, merma y sobreproducción')}
                    </h3>
                    <div className={articleUi.prose}>
                        <ul className={articleUi.dashList}>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.cocina.1Strong'), 'La ración del pase.')}</strong>{' '}
                                {t(
                                    k('list.cocina.1After'),
                                    'Si la ficha dice 200 gramos y salen 240, esos 40 los paga tu margen.',
                                )}
                            </li>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.cocina.2Strong'), 'La limpieza.')}</strong>{' '}
                                {t(
                                    k('list.cocina.2Before'),
                                    'Si el rendimiento real es peor que el de la ficha, falta producto sin que nadie lo tire. Para medirlo sin complicarte, ',
                                )}
                                <LearnArticleLink slug='mermas-hosteleria-prueba-papelera'>
                                    {t(k('list.cocina.2Link'), 'una semana apuntando lo que va al cubo te da el dato')}
                                </LearnArticleLink>
                                {t(k('list.cocina.2After'), '.')}
                            </li>
                            <li className={articleUi.dashItem}>
                                <strong>{t(k('list.cocina.3Strong'), 'Lo que se produce de más.')}</strong>{' '}
                                {t(k('list.cocina.3After'), 'Elaboraciones que caducan antes de venderse.')}
                            </li>
                        </ul>
                    </div>

                    <h3 id='sala' className={cn(articleUi.h3Block, 'scroll-mt-[90px]')}>
                        {t(k('h3.sala'), 'Sala: lo que sale sin pasar por caja')}
                    </h3>
                    <div className={articleUi.prose}>
                        <DashList
                            items={[
                                t(
                                    k('list.sala.1'),
                                    'Invitaciones sin marcar. La ronda de la casa, el chupito, el postre del cumpleaños.',
                                ),
                                t(k('list.sala.2'), 'Comida de personal sin apuntar.'),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.sala.1Before'),
                                'Si el descuadre se concentra en bebida, postres y pan, empieza aquí. Y ',
                            )}
                            <strong>{t(k('p.sala.1Strong'), 'lo último que miras son las personas.')}</strong>
                        </p>
                    </div>

                    <h2 id='a-favor' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.aFavor'), '¿Y si el inventario me sale a favor?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.aFavor.1Strong'), 'Un descuadre a favor suele ser un error:')}</strong>{' '}
                            {t(
                                k('p.aFavor.1After'),
                                'un albarán que no has metido, un conteo de más o una ficha que pone más cantidad de la que sirves.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.aFavor.2'),
                                'Si es lo último, el cliente está recibiendo menos de lo que pagas en tu escandallo. Merece la misma revisión que una pérdida.',
                            )}
                        </p>
                    </div>

                    <h2 id='pulpo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.pulpo'), 'El pulpo que nadie tiraba')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd, 'italic text-article-muted')}>
                            {t(
                                k('p.pulpo.lead'),
                                'Ejemplo inventado para enseñar la cuenta, con un patrón que todavía no hemos visto faltar en ningún local que hayamos revisado: un plato escandallado sin báscula. Pulpo cocido a 28 € el kilo sin IVA.',
                            )}
                        </p>
                    </div>
                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.pulpo.caption'), 'Pulpo a la gallega, un mes')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col' colSpan={2}>
                                        {t(k('table.pulpo.title'), 'Pulpo a la gallega, un mes')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pulpoRows.map(row => (
                                    <tr key={row.label}>
                                        <th scope='row'>{row.label}</th>
                                        <td className='tabular-nums'>
                                            {row.strong ? <strong>{row.value}</strong> : row.value}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.pulpo.1'),
                                'El jefe de cocina tenía razón: no se tiraba nada. El cocinero ponía 185 gramos cada vez, siempre igual.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.pulpo.2'),
                                'Los 150 gramos de la ficha los puso alguien a ojo, el día que se hizo la carta.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.pulpo.3Strong'), 'La ración no cuesta 4,20 €: cuesta 5,18 €.')}</strong>{' '}
                            {t(
                                k('p.pulpo.3Before'),
                                'A 16 € en carta, que son 14,55 € sin IVA, el food cost del plato no es el ',
                            )}
                            <strong>{t(k('p.pulpo.3Strong289'), '28,9 %')}</strong>
                            {t(k('p.pulpo.3Mid'), ', es el ')}
                            <strong>{t(k('p.pulpo.3Strong356'), '35,6 %')}</strong>
                            {t(k('p.pulpo.3After'), '.')}
                        </p>
                        <div className={cn(articleUi.stats, 'mt-2')}>
                            <div className={articleUi.statAlert}>
                                <p className={articleUi.statLabelAlert}>{t(k('stat.dev.label'), 'Desviación')}</p>
                                <p className={articleUi.statValueAlert}>{t(k('stat.dev.value'), '23 %')}</p>
                            </div>
                            <div className={articleUi.stat}>
                                <p className={articleUi.statLabel}>{t(k('stat.month.label'), 'Al mes')}</p>
                                <p className={articleUi.statValue}>{t(k('stat.month.value'), '215,60 €')}</p>
                            </div>
                            <div className={articleUi.statAccent}>
                                <p className={articleUi.statLabelAccent}>{t(k('stat.year.label'), 'Al año')}</p>
                                <p className={articleUi.statValueAccent}>{t(k('stat.year.value'), '2.600 €')}</p>
                            </div>
                        </div>
                        <p className={cn(articleUi.p, 'mt-2', articleUi.pGapMd)}>
                            <strong>{t(k('p.pulpo.4Strong'), 'Son unos 2.600 € al año.')}</strong>{' '}
                            {t(
                                k('p.pulpo.4After'),
                                'Y no es dinero que desaparece: es un precio puesto sobre un coste que no existía.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.pulpo.5'),
                                'Las salidas son tres: pesar la ración a 150 gramos, subir el precio o aceptar el margen sabiendo cuál es. Cualquiera vale si es una decisión.',
                            )}
                        </p>
                    </div>

                    <h2 id='limites' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.limites'), 'Lo que esta rutina no te dice')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.limites.1Strong'), 'Un mes es poco.')}</strong>{' '}
                            {t(
                                k('p.limites.1After'),
                                'Un pico aislado puede ser un error de conteo. Si el mismo ingrediente se desvía dos meses seguidos, ya no es casualidad.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(k('p.limites.2Strong'), 'Los ingredientes que van en todo no señalan nada.')}
                            </strong>{' '}
                            {t(
                                k('p.limites.2After'),
                                'Aceite, sal o harina se reparten entre tantos platos que la desviación no te dice dónde mirar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(k('p.limites.3Strong'), 'Lo que queda fuera de tus 20 ingredientes no lo ves.')}
                            </strong>{' '}
                            {t(
                                k('p.limites.3Before'),
                                'Revisa la lista cada trimestre con los albaranes, como contamos en ',
                            )}
                            <LearnArticleLink slug='como-hacer-inventario-hosteleria'>
                                {t(k('p.limites.3Link'), 'la guía para contar solo lo que pesa en euros')}
                            </LearnArticleLink>
                            {t(k('p.limites.3After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.limites.4Strong'),
                                    'Nuestro 3 % al 5 % se mide sobre lo que deberías haber gastado.',
                                )}
                            </strong>{' '}
                            {t(
                                k('p.limites.4After'),
                                'Varios fabricantes de software de cocina publican umbrales de entre 1 y 3 puntos, pero medidos sobre ventas. No se contradicen: con un food cost del 30 %, un 4 % de desviación es algo más de un punto sobre ventas.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.limites.5Strong'), 'Y te dice dónde, no quién.')}</strong>{' '}
                            {t(k('p.limites.5After'), 'Eso se habla con tu equipo, con los números ya revisados.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.limites.6'),
                                'Si tu descuadre pasa del 5 % y no sabes en cuál de los cuatro sitios mirar, escríbenos y le ponemos números: con revisar 20 ingredientes sabrás si las cosas están bien en tu negocio.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.limites.7'), 'Antes de hablar con nadie, coge la báscula.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Tu descuadre pasa del 5 %?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Escríbenos y le ponemos números: con revisar 20 ingredientes sabrás si las cosas están bien en tu negocio.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                    <p className={cn(articleUi.ctaClose, 'mt-6')}>
                        {t(k('cta.closeBefore'), 'Desde ')}
                        <TipiLink />
                        {t(k('cta.closeAfter'), ' te ayudamos a ver dónde se abre el hueco entre teórico y real.')}
                    </p>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>
                                {source.href ? (
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={articleUi.link}
                                    >
                                        {t(k(source.labelKey), source.fallback)}
                                    </a>
                                ) : (
                                    t(k(source.labelKey), source.fallback)
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
